import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import client, { uploadFile, formatApiError } from "@/lib/api";
import { Save, Trash2, Plus, ChevronDown, Image as ImageIcon } from "lucide-react";
import { detectMedia } from "@/lib/media";
import { AuthedImage } from "@/components/AuthedMedia";
import AdminPageHeader from "@/components/admin/AdminPageHeader";
import AdminButton from "@/components/admin/AdminButton";

const LANGS = ["en", "ur", "ta", "sw", "ms", "id", "hi"];

const emptyStory = (seq) => ({
  global_sequence: seq || 1,
  juz_number: 1,
  story_number_in_juz: 1,
  page_ref: "",
  title: { en: "" },
  video: { asset_url: "", duration_sec: 300 },
  storybook: [{ image_url: "", text: { en: "" } }],
  morals: [{ en: "" }],
  ayah: { arabic: "", transliteration: "", translations: { en: "" }, surah: "", ayah_number: 1 },
  word_of_the_day: { arabic: "", transliteration: "", meaning: { en: "" } },
  quiz: [{ question: { en: "" }, options: ["", "", "", ""], correct_index: 0 }],
  references: [],
});

// Layout borrowed from the Little Verses poem editor — pill tab bar,
// collapsible white sections, cream nested item cards, sticky save — colored
// with 600 Stories' own palette (hv-cream/navy/teal/coral/gold) instead of
// poems' green theme.

function Field({ label, small, children }) {
  return (
    <div>
      <label className={`block text-sm font-semibold text-hv-slate ${small ? "mb-1" : "mb-2"}`}>{label}</label>
      {children}
    </div>
  );
}

const inputCls = (small) => `w-full rounded-2xl border-2 border-[#E5DFC8] focus:border-hv-teal bg-hv-cream outline-none ${small ? "px-3 py-2" : "px-4 py-3 text-lg"} font-semibold`;

function CollapsibleSection({ title, subtitle, defaultOpen = false, testId, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="rounded-3xl bg-white shadow-sm overflow-hidden" data-testid={testId}>
      <button type="button" onClick={() => setOpen((o) => !o)}
              className="w-full flex items-center justify-between gap-3 px-6 sm:px-8 py-5 text-left">
        <div>
          <h3 className="hv-heading text-xl">{title}</h3>
          {subtitle && <p className="text-sm text-hv-slate mt-0.5">{subtitle}</p>}
        </div>
        <ChevronDown className={`w-5 h-5 text-hv-slate shrink-0 transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={2.5} />
      </button>
      {open && <div className="px-6 sm:px-8 pb-6 sm:pb-8 space-y-4">{children}</div>}
    </section>
  );
}

function ItemCard({ children, onRemove }) {
  return (
    <div className="rounded-2xl bg-hv-cream p-4 space-y-3">
      {children}
      <div className="flex justify-end">
        <button type="button" onClick={onRemove} className="text-hv-slate hover:text-hv-coral inline-flex items-center gap-1 text-sm font-semibold">
          <Trash2 className="w-4 h-4" strokeWidth={2.5} /> Remove
        </button>
      </div>
    </div>
  );
}

function AddButton({ onClick, children }) {
  return (
    <div className="flex justify-end -mt-1">
      <button type="button" onClick={onClick}
              className="inline-flex items-center gap-2 rounded-full bg-hv-gold text-hv-navy px-4 py-2 hv-heading text-sm min-h-[44px]">
        <Plus className="w-4 h-4" strokeWidth={3} /> {children}
      </button>
    </div>
  );
}

function FileUploadButton({ label, accept, busy, onChange, disabled }) {
  return (
    <label className={`inline-flex items-center gap-2 rounded-full bg-hv-tealTint px-4 py-2 text-sm font-semibold text-hv-teal cursor-pointer whitespace-nowrap ${busy || disabled ? "opacity-60 pointer-events-none" : ""}`}>
      {busy ? "Uploading…" : label}
      <input type="file" accept={accept} className="hidden" disabled={disabled}
             onChange={(e) => { const f = e.target.files?.[0]; if (f) onChange(f); e.target.value = ""; }} />
    </label>
  );
}

function ImagePreview({ src }) {
  return (
    <div className="rounded-2xl bg-white border-2 border-[#E5DFC8] w-14 h-14 flex items-center justify-center shrink-0 overflow-hidden">
      {src ? <AuthedImage src={src} className="w-full h-full object-cover" /> : <ImageIcon size={20} className="text-hv-slate" />}
    </div>
  );
}

const TAB_LABELS = [["content", "Content"], ["translations", "Translations"]];

export default function AdminStoryEditor() {
  const { seq } = useParams();
  const isNew = seq === "new" || seq === undefined;
  const location = useLocation();
  const navigate = useNavigate();

  const [form, setForm] = useState(() => emptyStory(location.state?.suggestedSeq));
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("content");
  const [activeLang, setActiveLang] = useState(LANGS[1]);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [uploading, setUploading] = useState("");

  useEffect(() => {
    if (isNew) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await client.get(`/admin/stories/${seq}`);
        setForm(data.story);
      } catch (ex) {
        setErr(formatApiError(ex.response?.data?.detail) || ex.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [seq, isNew]);

  const effectiveSeq = form.global_sequence;

  const save = async () => {
    setErr(""); setMsg(""); setSaving(true);
    try {
      const payload = {
        ...form,
        global_sequence: Number(form.global_sequence),
        juz_number: Number(form.juz_number),
        story_number_in_juz: Number(form.story_number_in_juz),
      };
      const { data } = await client.post("/stories", payload);
      setMsg(`Story ${payload.global_sequence} ${data.action}.`);
      if (isNew) navigate(`/admin/stories/${payload.global_sequence}`, { replace: true });
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete story ${effectiveSeq}? This also removes any uploaded media and progress records.`)) return;
    try {
      await client.delete(`/stories/${effectiveSeq}`);
      navigate("/admin/stories");
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    }
  };

  const doUpload = async (label, url, fieldName, file, onDone) => {
    setErr(""); setUploading(label);
    try {
      const data = await uploadFile(url, fieldName, file);
      onDone(data);
      setMsg(`${label} uploaded.`);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setUploading("");
    }
  };

  const removeMedia = async (label, url, onDone) => {
    setErr("");
    try {
      await client.delete(url);
      onDone();
      setMsg(`${label} removed.`);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    }
  };

  const updateArray = (key, idx, value) => {
    const next = [...(form[key] || [])];
    next[idx] = value;
    setForm((f) => ({ ...f, [key]: next }));
  };
  const removeArrayItem = (key, idx) => setForm((f) => ({ ...f, [key]: f[key].filter((_, i) => i !== idx) }));
  const addArrayItem = (key, item) => setForm((f) => ({ ...f, [key]: [...(f[key] || []), JSON.parse(JSON.stringify(item))] }));

  if (loading) return <div className="text-hv-slate">Loading…</div>;

  return (
    <div data-testid="admin-story-editor" className="space-y-6 pb-24">
      <AdminPageHeader
        title={isNew ? "New story" : `Story #${effectiveSeq}`}
        subtitle={form.title?.en || "Untitled"}
        actions={!isNew && (
          <AdminButton variant="destructive" icon={Trash2} onClick={remove} data-testid="editor-delete">
            Delete story
          </AdminButton>
        )}
      />

      <div className="flex gap-2 rounded-full bg-white p-1 w-max shadow-sm" data-testid="editor-tabs">
        {TAB_LABELS.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)} data-testid={`editor-tab-${k}`}
                  className={`rounded-full px-5 py-2 hv-heading text-sm ${tab === k ? "bg-hv-gold text-hv-navy" : "text-hv-slate hover:text-hv-navy"}`}>
            {l}
          </button>
        ))}
      </div>

      {err && <div className="text-hv-coral font-bold text-sm" data-testid="editor-error">{err}</div>}
      {msg && <div className="text-hv-teal font-bold text-sm" data-testid="editor-msg">{msg}</div>}

      {tab === "content" && (
        <div className="space-y-4">
          <CollapsibleSection title="Basics" defaultOpen testId="section-basics">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Global sequence (1-600)" small>
                <input type="number" min="1" max="600" required value={form.global_sequence} data-testid="editor-seq"
                       onChange={(e) => setForm((f) => ({ ...f, global_sequence: e.target.value }))} className={inputCls(true)} />
              </Field>
              <Field label="Page reference" small>
                <input value={form.page_ref || ""} data-testid="editor-page-ref"
                       onChange={(e) => setForm((f) => ({ ...f, page_ref: e.target.value }))} className={inputCls(true)} />
              </Field>
              <Field label="Juz number (1-30)" small>
                <input type="number" min="1" max="30" required value={form.juz_number} data-testid="editor-juz"
                       onChange={(e) => setForm((f) => ({ ...f, juz_number: e.target.value }))} className={inputCls(true)} />
              </Field>
              <Field label="Story number in Juz" small>
                <input type="number" min="1" max="20" required value={form.story_number_in_juz} data-testid="editor-story-num"
                       onChange={(e) => setForm((f) => ({ ...f, story_number_in_juz: e.target.value }))} className={inputCls(true)} />
              </Field>
            </div>
            <Field label="Title (English)">
              <input required value={form.title?.en || ""} data-testid="editor-title"
                     onChange={(e) => setForm((f) => ({ ...f, title: { ...f.title, en: e.target.value } }))} className={inputCls(false)} />
            </Field>
          </CollapsibleSection>

          <CollapsibleSection title="Media" subtitle="Cover image and the story video" testId="section-media">
            <div className="flex items-center gap-3 flex-wrap">
              <ImagePreview src={form.cover_image_gridfs_id ? `/api/stories/${effectiveSeq}/media/cover-image` : null} />
              <FileUploadButton label="Upload cover image" accept="image/*" disabled={isNew} busy={uploading === "Cover image"}
                onChange={(file) => doUpload("Cover image", `/admin/stories/${effectiveSeq}/cover-image`, "image", file,
                  (data) => setForm((f) => ({ ...f, cover_image_gridfs_id: data.cover_image_gridfs_id })))} />
              {form.cover_image_gridfs_id && (
                <button type="button" className="text-xs font-semibold text-hv-coral underline"
                        onClick={() => removeMedia("Cover image", `/admin/stories/${effectiveSeq}/cover-image`,
                          () => setForm((f) => ({ ...f, cover_image_gridfs_id: null })))}>
                  Remove uploaded image
                </button>
              )}
            </div>
            <Field label="Video URL (used if no uploaded video file)" small>
              <input value={form.video?.asset_url || ""} data-testid="editor-video-url"
                     placeholder="https://youtube.com/watch?v=…, https://vimeo.com/…, https://drive.google.com/file/d/…, or a direct .mp4 link"
                     onChange={(e) => setForm((f) => ({ ...f, video: { ...f.video, asset_url: e.target.value } }))} className={inputCls(true)} />
              {form.video?.asset_url && (
                <p className="text-xs text-hv-slate mt-1">Detected: <span className="font-semibold text-hv-navy">{detectMedia(form.video.asset_url).type}</span></p>
              )}
            </Field>
            <div className="flex items-center gap-3 flex-wrap">
              <FileUploadButton label={form.video_gridfs_id ? "Replace video" : "Upload video"} accept="video/*" disabled={isNew}
                busy={uploading === "Video"}
                onChange={(file) => doUpload("Video", `/admin/stories/${effectiveSeq}/video`, "video", file,
                  (data) => setForm((f) => ({ ...f, video_gridfs_id: data.video_gridfs_id })))} />
              {form.video_gridfs_id && (
                <>
                  <a href={`/api/stories/${effectiveSeq}/media/video`} target="_blank" rel="noreferrer" className="text-xs font-semibold text-hv-teal underline">
                    View current video
                  </a>
                  <button type="button" className="text-xs font-semibold text-hv-coral underline"
                          onClick={() => removeMedia("Video", `/admin/stories/${effectiveSeq}/video`,
                            () => setForm((f) => ({ ...f, video_gridfs_id: null })))}>
                    Remove video
                  </button>
                </>
              )}
            </div>
            <Field label="Duration (seconds)" small>
              <input type="number" min="1" value={form.video?.duration_sec || ""} data-testid="editor-video-duration"
                     onChange={(e) => setForm((f) => ({ ...f, video: { ...f.video, duration_sec: e.target.value } }))} className={inputCls(true)} />
            </Field>
          </CollapsibleSection>

          <CollapsibleSection title="Storybook" subtitle="Page-flip images and text" testId="section-storybook">
            <AddButton onClick={() => addArrayItem("storybook", { image_url: "", text: { en: "" } })}>Add page</AddButton>
            <div className="space-y-3">
              {(form.storybook || []).map((page, idx) => (
                <ItemCard key={idx} onRemove={() => removeArrayItem("storybook", idx)}>
                  <div className="flex items-center gap-3 flex-wrap">
                    <ImagePreview src={page.image_gridfs_id ? `/api/stories/${effectiveSeq}/media/storybook/${idx}` : null} />
                    <FileUploadButton label={`Upload page ${idx + 1} image`} accept="image/*" disabled={isNew}
                      busy={uploading === `Storybook image ${idx}`}
                      onChange={(file) => doUpload(`Storybook image ${idx}`, `/admin/stories/${effectiveSeq}/storybook/${idx}/image`, "image", file,
                        (data) => updateArray("storybook", idx, { ...page, image_gridfs_id: data.image_gridfs_id }))} />
                  </div>
                  <Field label="Image URL (used if no uploaded image)" small>
                    <input value={page.image_url || ""} onChange={(e) => updateArray("storybook", idx, { ...page, image_url: e.target.value })} className={inputCls(true)} />
                  </Field>
                  <Field label="Page text (English)" small>
                    <textarea rows={3} value={page.text?.en || ""}
                              onChange={(e) => updateArray("storybook", idx, { ...page, text: { ...page.text, en: e.target.value } })} className={inputCls(true)} />
                  </Field>
                </ItemCard>
              ))}
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="Morals" testId="section-morals">
            <AddButton onClick={() => addArrayItem("morals", { en: "" })}>Add moral</AddButton>
            <div className="space-y-3">
              {(form.morals || []).map((moral, idx) => (
                <ItemCard key={idx} onRemove={() => removeArrayItem("morals", idx)}>
                  <Field label={`Moral ${idx + 1} (English)`} small>
                    <input value={moral.en || ""} onChange={(e) => updateArray("morals", idx, { ...moral, en: e.target.value })} className={inputCls(true)} />
                  </Field>
                </ItemCard>
              ))}
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="Ayah" testId="section-ayah">
            <Field label="Arabic" small><input dir="rtl" value={form.ayah?.arabic || ""}
              onChange={(e) => setForm((f) => ({ ...f, ayah: { ...f.ayah, arabic: e.target.value } }))} className={inputCls(true)} /></Field>
            <Field label="Transliteration" small><input value={form.ayah?.transliteration || ""}
              onChange={(e) => setForm((f) => ({ ...f, ayah: { ...f.ayah, transliteration: e.target.value } }))} className={inputCls(true)} /></Field>
            <Field label="Translation (English)" small><input value={form.ayah?.translations?.en || ""}
              onChange={(e) => setForm((f) => ({ ...f, ayah: { ...f.ayah, translations: { ...f.ayah?.translations, en: e.target.value } } }))} className={inputCls(true)} /></Field>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Surah" small><input value={form.ayah?.surah || ""}
                onChange={(e) => setForm((f) => ({ ...f, ayah: { ...f.ayah, surah: e.target.value } }))} className={inputCls(true)} /></Field>
              <Field label="Ayah number" small><input type="number" value={form.ayah?.ayah_number || ""}
                onChange={(e) => setForm((f) => ({ ...f, ayah: { ...f.ayah, ayah_number: e.target.value } }))} className={inputCls(true)} /></Field>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <FileUploadButton label={form.ayah_audio_gridfs_id ? "Replace audio" : "Upload audio"} accept="audio/*" disabled={isNew}
                busy={uploading === "Ayah audio"}
                onChange={(file) => doUpload("Ayah audio", `/admin/stories/${effectiveSeq}/ayah-audio`, "audio", file,
                  (data) => setForm((f) => ({ ...f, ayah_audio_gridfs_id: data.audio_gridfs_id })))} />
              {form.ayah_audio_gridfs_id && (
                <>
                  <audio controls className="h-9" src={`/api/stories/${effectiveSeq}/media/ayah-audio`} />
                  <button type="button" className="text-xs font-semibold text-hv-coral underline"
                          onClick={() => removeMedia("Ayah audio", `/admin/stories/${effectiveSeq}/ayah-audio`,
                            () => setForm((f) => ({ ...f, ayah_audio_gridfs_id: null })))}>
                    Remove audio
                  </button>
                </>
              )}
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="Word of the Day" testId="section-word">
            <Field label="Arabic" small><input dir="rtl" value={form.word_of_the_day?.arabic || ""}
              onChange={(e) => setForm((f) => ({ ...f, word_of_the_day: { ...f.word_of_the_day, arabic: e.target.value } }))} className={inputCls(true)} /></Field>
            <Field label="Transliteration" small><input value={form.word_of_the_day?.transliteration || ""}
              onChange={(e) => setForm((f) => ({ ...f, word_of_the_day: { ...f.word_of_the_day, transliteration: e.target.value } }))} className={inputCls(true)} /></Field>
            <Field label="Meaning (English)" small><input value={form.word_of_the_day?.meaning?.en || ""}
              onChange={(e) => setForm((f) => ({ ...f, word_of_the_day: { ...f.word_of_the_day, meaning: { ...f.word_of_the_day?.meaning, en: e.target.value } } }))} className={inputCls(true)} /></Field>
            <div className="flex items-center gap-3 flex-wrap">
              <FileUploadButton label={form.word_audio_gridfs_id ? "Replace audio" : "Upload audio"} accept="audio/*" disabled={isNew}
                busy={uploading === "Word audio"}
                onChange={(file) => doUpload("Word audio", `/admin/stories/${effectiveSeq}/word-audio`, "audio", file,
                  (data) => setForm((f) => ({ ...f, word_audio_gridfs_id: data.audio_gridfs_id })))} />
              {form.word_audio_gridfs_id && (
                <>
                  <audio controls className="h-9" src={`/api/stories/${effectiveSeq}/media/word-audio`} />
                  <button type="button" className="text-xs font-semibold text-hv-coral underline"
                          onClick={() => removeMedia("Word audio", `/admin/stories/${effectiveSeq}/word-audio`,
                            () => setForm((f) => ({ ...f, word_audio_gridfs_id: null })))}>
                    Remove audio
                  </button>
                </>
              )}
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="Quiz" testId="section-quiz">
            <p className="text-xs text-hv-slate mb-2">10 questions per story: 2 from the story, 3 from the Qur'anic incident, 3 morals, 1 ayah, 1 word of the day.</p>
            <AddButton onClick={() => addArrayItem("quiz", { category: "modern_story", question: { en: "" }, options: ["", "", "", ""], correct_index: 0 })}>Add question</AddButton>
            <div className="space-y-3">
              {(form.quiz || []).map((question, idx) => (
                <ItemCard key={idx} onRemove={() => removeArrayItem("quiz", idx)}>
                  <Field label="Category" small>
                    <select value={question.category || "modern_story"}
                            onChange={(e) => updateArray("quiz", idx, { ...question, category: e.target.value })} className={inputCls(true)}>
                      <option value="modern_story">From the Story (modern narration)</option>
                      <option value="quranic_incident">Qur'anic Incident</option>
                      <option value="moral">Moral Lesson</option>
                      <option value="ayah">Ayah of the Day</option>
                      <option value="word_of_day">Word of the Day</option>
                    </select>
                  </Field>
                  <Field label={`Question ${idx + 1} (English)`} small>
                    <input value={question.question?.en || ""}
                           onChange={(e) => updateArray("quiz", idx, { ...question, question: { ...question.question, en: e.target.value } })} className={inputCls(true)} />
                  </Field>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {(question.options || ["", "", "", ""]).map((opt, oi) => (
                      <div key={oi} className="flex items-center gap-2">
                        <input type="radio" checked={question.correct_index === oi}
                               onChange={() => updateArray("quiz", idx, { ...question, correct_index: oi })} className="accent-hv-teal" />
                        <input value={opt} placeholder={`Option ${oi + 1}`}
                               onChange={(e) => {
                                 const options = [...(question.options || ["", "", "", ""])];
                                 options[oi] = e.target.value;
                                 updateArray("quiz", idx, { ...question, options });
                               }} className={inputCls(true)} />
                      </div>
                    ))}
                  </div>
                </ItemCard>
              ))}
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="References" testId="section-references">
            <AddButton onClick={() => addArrayItem("references", { label: "", link_or_note: "" })}>Add reference</AddButton>
            <div className="space-y-3">
              {(form.references || []).map((ref, idx) => (
                <ItemCard key={idx} onRemove={() => removeArrayItem("references", idx)}>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Field label="Label" small><input value={ref.label || ""} onChange={(e) => updateArray("references", idx, { ...ref, label: e.target.value })} className={inputCls(true)} /></Field>
                    <Field label="Link or note" small><input value={ref.link_or_note || ""} onChange={(e) => updateArray("references", idx, { ...ref, link_or_note: e.target.value })} className={inputCls(true)} /></Field>
                  </div>
                </ItemCard>
              ))}
            </div>
          </CollapsibleSection>
        </div>
      )}

      {tab === "translations" && (
        <div className="space-y-6">
          <div className="flex flex-wrap gap-2" data-testid="editor-lang-tabs">
            {LANGS.filter((l) => l !== "en").map((l) => (
              <button key={l} type="button" onClick={() => setActiveLang(l)}
                      className={`rounded-full px-4 py-2 hv-heading text-sm ${activeLang === l ? "bg-hv-coral text-white" : "bg-white text-hv-navy hover:bg-hv-cream"}`}>
                {l.toUpperCase()}
              </button>
            ))}
          </div>

          <CollapsibleSection title={`Story — ${activeLang.toUpperCase()}`} defaultOpen testId="section-translations-story">
            <Field label="Title" small>
              <input dir={activeLang === "ur" ? "rtl" : "ltr"} value={form.title?.[activeLang] || ""}
                     onChange={(e) => setForm((f) => ({ ...f, title: { ...f.title, [activeLang]: e.target.value } }))} className={inputCls(true)} />
            </Field>
            {(form.storybook || []).map((page, idx) => (
              <Field key={idx} label={`Storybook page ${idx + 1} text`} small>
                <textarea rows={3} dir={activeLang === "ur" ? "rtl" : "ltr"} value={page.text?.[activeLang] || ""}
                          onChange={(e) => updateArray("storybook", idx, { ...page, text: { ...page.text, [activeLang]: e.target.value } })} className={inputCls(true)} />
              </Field>
            ))}
            {(form.morals || []).map((moral, idx) => (
              <Field key={idx} label={`Moral ${idx + 1}`} small>
                <input dir={activeLang === "ur" ? "rtl" : "ltr"} value={moral[activeLang] || ""}
                       onChange={(e) => updateArray("morals", idx, { ...moral, [activeLang]: e.target.value })} className={inputCls(true)} />
              </Field>
            ))}
            <Field label="Ayah translation" small>
              <input dir={activeLang === "ur" ? "rtl" : "ltr"} value={form.ayah?.translations?.[activeLang] || ""}
                     onChange={(e) => setForm((f) => ({ ...f, ayah: { ...f.ayah, translations: { ...f.ayah?.translations, [activeLang]: e.target.value } } }))} className={inputCls(true)} />
            </Field>
            <Field label="Word of the Day — meaning" small>
              <input dir={activeLang === "ur" ? "rtl" : "ltr"} value={form.word_of_the_day?.meaning?.[activeLang] || ""}
                     onChange={(e) => setForm((f) => ({ ...f, word_of_the_day: { ...f.word_of_the_day, meaning: { ...f.word_of_the_day?.meaning, [activeLang]: e.target.value } } }))} className={inputCls(true)} />
            </Field>
          </CollapsibleSection>

          <CollapsibleSection title={`Quiz — ${activeLang.toUpperCase()}`} testId="section-translations-quiz">
            {(form.quiz || []).map((question, idx) => (
              <Field key={idx} label={`Question ${idx + 1}`} small>
                <input dir={activeLang === "ur" ? "rtl" : "ltr"} value={question.question?.[activeLang] || ""}
                       onChange={(e) => updateArray("quiz", idx, { ...question, question: { ...question.question, [activeLang]: e.target.value } })} className={inputCls(true)} />
              </Field>
            ))}
          </CollapsibleSection>
        </div>
      )}

      <div className="sticky bottom-4 flex justify-end">
        <button type="button" onClick={save} disabled={saving} data-testid="editor-save"
                className="inline-flex items-center justify-center gap-3 rounded-full bg-hv-coral text-white px-8 py-4 min-h-[64px] hv-heading text-lg shadow-lg shadow-black/10 disabled:opacity-50">
          <Save className="w-5 h-5" strokeWidth={2.5} /> {saving ? "Saving…" : "Save all"}
        </button>
      </div>
    </div>
  );
}
